import { defineStore, acceptHMRUpdate } from 'pinia'
import siteConfig from '@/config.json'
import axios from 'axios'
import uniq from 'lodash/uniq'
import { useInstructorStore } from '@/stores/InstructorStore'
import { useUserStore } from '@/stores/UserStore'
import { getInstructorImageUrl, normalize } from './services/instructor'
import {
  usDateFormatToYYYYMMDD,
  usDateFormatToShortMonthAndDay,
  timestampsToDateRange,
  getDateStandard,
  getYearsByUnit,
} from '@/helpers/datetime'
import { capitalizeFirstLetter, cleanMarkup } from '@/helpers/text'
import { useCartStore } from '@/stores/CartStore'
import { classImageUrl, presentImage } from '@/helpers/images'

const getClassStartSortKey = (cls) => {
  if (cls.startDateStandard) return cls.startDateStandard
  if (!cls.startDate) return ''
  const time = new Date(cls.startDate).getTime()
  return Number.isNaN(time) ? '' : String(time).padStart(15, '0')
}

const sortByStartDate = (a, b) => {
  const keyA = getClassStartSortKey(a)
  const keyB = getClassStartSortKey(b)
  if (keyA < keyB) return -1
  if (keyA > keyB) return 1
  return 0
}

const sortClassDaysByStart = (a, b) => {
  const aKey = `${usDateFormatToYYYYMMDD(a.dateFmt)}-${a.stTime}`
  const bKey = `${usDateFormatToYYYYMMDD(b.dateFmt)}-${b.stTime}`
  if (aKey < bKey) return -1
  if (aKey > bKey) return 1
  return 0
}

const sortByCode = (a, b) => {
  const codeA = (a.code ?? '').trim()
  const codeB = (b.code ?? '').trim()

  return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' })
}

const sortByTitle = (a, b) => {
  const titleA = (a.shortTitle || a.title || '').trim().toLowerCase()
  const titleB = (b.shortTitle || b.title || '').trim().toLowerCase()
  return titleA.localeCompare(titleB)
}

export const sortByOpenRegistrationFirst = (a, b) => {
  const aClosed = !!a.isPastCutOffDate
  const bClosed = !!b.isPastCutOffDate
  if (aClosed === bClosed) return 0
  return aClosed ? 1 : -1
}

const sortByStartDateWithMultiDayFirst = (a, b) => {
  const dateDiff = sortByStartDate(a, b)

  if (dateDiff !== 0) return dateDiff

  const aIsMultiDay = a.startDate !== a.endDate
  const bIsMultiDay = b.startDate !== b.endDate

  if (aIsMultiDay === bIsMultiDay) return 0
  return aIsMultiDay ? -1 : 1
}

/**
 * Combined list comparator for availability / open-registration feature sorts.
 * Open registration stays above closed even when available-above-full re-sorts by date
 * (ACCHD-9871).
 */
export const buildClassListComparator = () => {
  const preferOpenRegistration = !!siteConfig.features.showOpenClassesAboveClosed
  const multiDayFirst = !!siteConfig.features.showMultiDayClassesAboveSingleDay

  return (a, b) => {
    if (preferOpenRegistration) {
      const openCmp = sortByOpenRegistrationFirst(a, b)
      if (openCmp !== 0) return openCmp
    }

    const dateCmp = multiDayFirst ? sortByStartDateWithMultiDayFirst(a, b) : sortByStartDate(a, b)
    if (dateCmp !== 0) return dateCmp

    return sortByTitle(a, b)
  }
}

const getRegistrationCloseMs = () => {
  const hours = siteConfig.features?.registrationCloseHoursBefore

  if (typeof hours === 'number' && hours > 0) {
    return hours * 60 * 60 * 1000
  }

  return null
}

const parseUsDate = (dateStr) => {
  const [monthStr, dayStr, yearStrRaw] = dateStr.split('/')
  const month = Number(monthStr) - 1
  const day = Number(dayStr)
  const year = yearStrRaw.length === 2 ? Number(`20${yearStrRaw}`) : Number(yearStrRaw)

  return { year, month, day }
}

const parseTime12h = (timeStr) => {
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) return null

  let [, hStr, mStr, period] = match
  let hours = Number(hStr)
  const minutes = Number(mStr)
  const isPM = period.toUpperCase() === 'PM'

  if (isPM && hours !== 12) hours += 12
  if (!isPM && hours === 12) hours = 0

  return { hours, minutes }
}

/**
 * Canvas often stamps the series registration window onto every session day
 * (typically the day before series start). With allowRegistrationForStartedClasses,
 * that default stamp must not close the remaining sessions of a series (ACCHD-9932).
 * Intentional closes after the series start (e.g. Nimbus Daytime Art Club / ACCHD-9871)
 * and day-sales drop-ins still honor each session's own close.
 */
export const hasOnlyPreStartSeriesCloses = (cls, futurePublishedDays) => {
  if (!siteConfig.features?.allowRegistrationForStartedClasses) return false
  if (cls.daySales || !cls.startDateStandard || !futurePublishedDays?.length) return false

  return futurePublishedDays.every((d) => {
    if (!d.registrationCloseDate) return true
    const closeStandard = getDateStandard(new Date(d.registrationCloseDate).toISOString())
    return closeStandard <= cls.startDateStandard
  })
}

export const resolveClassDayIsPastCutOffDate = (d, { ignoreRegistrationCloseDate = false } = {}) => {
  if (d.registrationCloseDate && !ignoreRegistrationCloseDate) {
    return Date.now() >= new Date(d.registrationCloseDate).getTime()
  }

  const registrationCloseMs = getRegistrationCloseMs()
  const time = parseTime12h(d.startTimeFmt)

  if (registrationCloseMs === null || !time) {
    return false
  }

  const { year, month, day } = parseUsDate(d.dateFmt)
  const { hours, minutes } = time

  const classStart = new Date(year, month, day, hours, minutes, 0, 0)
  const msUntilClass = classStart.getTime() - Date.now()

  return msUntilClass < registrationCloseMs
}

export const applyMembershipDiscount = (cls, discount) => {
  const discountAmount = discount.type === 'percent' ? cls.costNon * (discount.amount / 100) : discount.value
  cls.costMember = parseFloat((cls.costNon - discountAmount).toFixed(2))
  cls.costMemberStr = String(cls.costMember)
  cls.memberCostFmt = String(cls.costMember.toFixed(2))
  cls.memberSavings = parseFloat((cls.costNon - cls.costMember).toFixed(2))

  if (cls.sessionCostNon >= 0) {
    const sessionDiscountAmount =
      discount.type === 'percent' ? cls.sessionCostNon * (discount.amount / 100) : discount.value
    cls.sessionCostMem = parseFloat((cls.sessionCostNon - sessionDiscountAmount).toFixed(2))
  }
}

export const resolveallowRegistrationAfterStart = (cls) =>
  !cls.hasStarted || !!siteConfig.features.allowRegistrationForStartedClasses

export const resolveCanAddEntireSeries = (cls) => {
  let canAddEntireSeries = cls.costMember >= 0 || cls.costNon >= 0

  if (siteConfig.features.hideSeriesWhenDropInsAreCheaper && cls.daySales && canAddEntireSeries) {
    const remaining = cls.classDays?.length || 0
    canAddEntireSeries =
      (cls.costMember >= 0 && cls.costMember <= remaining * (cls.sessionCostMem || 0)) ||
      (cls.costNon >= 0 && cls.costNon <= remaining * (cls.sessionCostNon || 0))
  }

  return canAddEntireSeries
}

const getClassImageUrl = (imageUrl) => classImageUrl(imageUrl, siteConfig)

const daySortPos = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
  Saturday: 5,
  Sunday: 6,
}

export const selectedFilterValues = (value) => {
  if (value == null || value === '') return []
  const values = Array.isArray(value) ? value : [value]
  return values.map((item) => String(item).trim()).filter(Boolean)
}

export const parseClassKeywords = (keywords) => {
  if (keywords == null || keywords === '') return []
  const values = Array.isArray(keywords) ? keywords : String(keywords).split(',')
  return values.map((item) => String(item).trim()).filter(Boolean)
}

const splitKeywordWords = (keyword) => String(keyword).trim().split(/\s+/).filter(Boolean)

export const keywordKey = (keyword) => splitKeywordWords(keyword).join(' ').toLowerCase()

const titleCaseKeywordWord = (word) => (/[A-Z]/.test(word.slice(1)) ? word : capitalizeFirstLetter(word.toLowerCase()))

const countUppercase = (value) => value.replace(/[^A-Z]/g, '').length

export const normalizeKeywordLabel = (keyword, casing = 'titlecase') => {
  const words = splitKeywordWords(keyword)
  if (!words.length) return ''
  if (casing === 'lowercase') return words.join(' ').toLowerCase()
  return words.map(titleCaseKeywordWord).join(' ')
}

export const collectUniqueKeywords = (
  classes,
  casing = siteConfig.features?.classFilters?.keywordCasing || 'titlecase'
) => {
  const labels = new Map()
  for (const keyword of classes.flatMap((cls) => parseClassKeywords(cls?.keywords))) {
    const label = normalizeKeywordLabel(keyword, casing)
    if (!label) continue
    const key = keywordKey(label)
    const current = labels.get(key)
    const uppercaseDiff = current == null ? 1 : countUppercase(label) - countUppercase(current)
    if (uppercaseDiff > 0 || (uppercaseDiff === 0 && label < current)) labels.set(key, label)
  }
  return [...labels.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
}

export const classMatchesKeywordsFilter = (classKeywords, selectedKeywords) => {
  const selected = selectedFilterValues(selectedKeywords).map(keywordKey)
  if (!selected.length) return true

  const parsed = parseClassKeywords(classKeywords).map(keywordKey)
  return parsed.some((keyword) => selected.includes(keyword))
}

export const areaOfFocusForClass = (cls, program, field) => {
  if (!field) return ''
  const eventField = field === 'udef1' ? 'eventUdef1' : field === 'udef2' ? 'eventUdef2' : null
  const fromProgram = program?.[field]
  const fromClass = eventField ? cls?.[eventField] : null
  return String(fromProgram || fromClass || '').trim()
}

const noFilters = {
  semester: null,
  sessions: null,
  site: null,
  location: null,
  locationType: null,
  category: null,
  medium: null,
  program: null,
  // duration: null,
  level: null,
  instructor: null,
  day: null,
  dateRange: null,
  type: null,
  ageRange: null,
  availability: null,
  keywords: null,
  areaOfFocus: null,
}

export const useClassStore = defineStore('ClassStore', {
  state: () => {
    return {
      openClasses: [],
      openClassesFetch: null,
      openWorkshops: [],
      openClassProgramIds: [],
      programs: [],
      programIdsByName: {},
      semesters: [],
      openSemesters: new Map(),
      mediums: [],
      programMediums: [],
      secondaryMediums: [],
      categories: [],
      levels: [],
      locations: [],
      sites: [],
      keywords: [],
      filters: noFilters,
      sortBy:
        !siteConfig.features.showAvailableClassesAboveFull && siteConfig.features.defaultSorting
          ? siteConfig.features.defaultSorting
          : null,
      searchQuery: null,
      hiddenFilters: { division: null },
      loadingClasses: false,
      loadingPrograms: false,
      loadingInstructors: false,
      classRegistrationCountsById: {},
      sessionRegistrationCountsById: {},
      roster: {},
      loadingRoster: false,
    }
  },

  getters: {
    loading() {
      const instructorStore = useInstructorStore()

      return this.loadingClasses || instructorStore.loadingInstructors || this.loadingPrograms
    },

    programsWithOpenClasses() {
      const programs = this.programs.filter((p) => this.openClassProgramIds.includes(p.eventId)).map((p) => p.title)

      if (siteConfig.features.programOrder) {
        const order = siteConfig.features.programOrder
        return programs.sort((a, b) => {
          if (order.indexOf(a) > order.indexOf(b)) return 1
          if (order.indexOf(a) < order.indexOf(b)) return -1
          return 0
        })
      } else {
        return programs
      }
    },

    programsById() {
      if (!this.programs.length) return {}

      if (!this.openClasses.length) return {}

      const programMap = {}

      this.programs.forEach((program) => {
        programMap[program.eventId] = program
      })

      return programMap
    },

    queryParams(state) {
      const params = {}
      Object.keys(this.filters).forEach((filter) => {
        if (this.filters[filter] !== null) {
          params[filter] = this.filters[filter]
        }
      })

      return params
    },

    search(state) {
      const searchFields = ['title', 'description', 'keywords']
      if (!this.searchQuery) return this.openClasses
      return this.openClasses.filter((c) => {
        return searchFields.some((field) => c[field]?.toLowerCase().includes(this.searchQuery.toLowerCase()))
      })
    },

    sortByOptions() {
      const configOptions = siteConfig.features?.classFilters?.sortByOptions

      return [
        { text: 'Artist Name', value: 'instructorSummary' },
        { text: 'Title', value: 'shortTitle' },
        { text: 'Start Date', value: 'startDateStandard' },
        ...(configOptions ? configOptions : []),
      ]
    },
    sortByText() {
      const option = this.sortByOptions.find((option) => option.value === this.sortBy)

      return option ? option.text : ''
    },
    filteredList(state) {
      const instructorStore = useInstructorStore()

      let list = [...this.search]

      if (!siteConfig.features.showClassesAfterCutOffDate) {
        list = list.filter((c) => !c.isPastCutOffDate)
      }

      if (this.filters.dateRange) {
        const parts = this.filters.dateRange.split('_')
        const startDate = parts[0]

        if (parts.length === 2) {
          const endDate = parts[1]
          list = list.filter((c) => {
            if (c.daySales || siteConfig.features.classFilters?.showStartedClassesInDateRangeFilter) {
              const validDays = c.classDays.filter((d) => {
                return d.dateStandard >= startDate && d.dateStandard <= endDate
              })
              return validDays.length > 0
            } else {
              return c.startDateStandard >= startDate && c.startDateStandard <= endDate
            }
          })
        } else {
          list = list.filter((c) => {
            if (c.daySales || siteConfig.features.classFilters?.showStartedClassesInDateRangeFilter) {
              const validDays = c.classDays.filter((d) => {
                return d.dateStandard >= startDate
              })
              return validDays.length > 0
            } else {
              return c.startDateStandard >= startDate
            }
          })
        }
      }

      if (this.filters.ageRange && siteConfig.features.classFilters?.ageRangeFilters) {
        const rules = siteConfig.features.classFilters?.ageRangeFilters?.find((f) =>
          f.name.includes(this.filters.ageRange)
        )

        list = list.filter((item) => {
          if (!item.minAge || !item.maxAge) return true

          const minAgeYears = getYearsByUnit(item.minAge, item.ageUnitMin)
          const maxAgeYears = getYearsByUnit(item.maxAge, item.ageUnitMax)

          if (minAgeYears > rules.maxAge) return false

          if (maxAgeYears < rules.minAge) return false

          return true
        })
      }

      if (this.hiddenFilters.division) {
        const selectedDivisions = Array.isArray(this.hiddenFilters.division)
          ? this.hiddenFilters.division
          : [this.hiddenFilters.division]

        const divisionsFromConfig = selectedDivisions.flatMap((item) => {
          return siteConfig.features.separateClassesByDivision?.[item] || [item]
        })

        list = list.filter((c) => divisionsFromConfig.includes(c.division))
      }

      if (this.filters.type) {
        const selectedTypes = selectedFilterValues(this.filters.type)
        list = list.filter((c) => selectedTypes.includes(c.division?.trim()))
      }

      if (this.filters.semester) {
        list = list.filter((c) => this.filters.semester.includes(c.semesterName.trim()))
      }

      if (this.filters.site) {
        list = list.filter((c) => this.filters.site.includes(c.site))
      }

      if (this.filters.location) {
        list = list.filter((c) => this.filters.location.includes(c.location?.trim()))
      }

      if (this.filters.locationType) {
        list = list.filter((c) => this.filters.locationType.includes(c.locationType))
      }

      if (this.filters.category) {
        list = list.filter((c) => this.filters.category.includes(c.department?.trim()))
      }

      if (this.filters.sessions && this.filters.sessions !== 'Any sessions') {
        const selected = Array.isArray(this.filters.sessions) ? this.filters.sessions[0] : this.filters.sessions

        const sessionFilters = siteConfig?.features?.classFilters?.sessionFilters ?? []

        const rule = sessionFilters.find((f) => f.name === selected)

        if (rule) {
          const min = rule.minSessions ?? 0
          const max = rule.maxSessions ?? Number.POSITIVE_INFINITY

          list = list.filter((c) => {
            const sessionsCount = Number(c.sessions ?? 0)
            return sessionsCount >= min && sessionsCount <= max
          })
        }
      }

      if (this.filters.program) {
        const selectedPrograms = selectedFilterValues(this.filters.program)
        list = list.filter((c) => selectedPrograms.some((program) => this.programIdsByName[program] === c.eventId))
      }

      if (this.filters.areaOfFocus) {
        const selectedFocus = selectedFilterValues(this.filters.areaOfFocus).map((value) => value.toLowerCase())
        const field = siteConfig.features.classFilters?.areaOfFocusField
        list = list.filter((c) => {
          const focus = areaOfFocusForClass(c, this.programsById[c.eventId], field).toLowerCase()
          return selectedFocus.includes(focus)
        })
      }

      if (this.filters.medium) {
        const selectedMediums = Array.isArray(this.filters.medium) ? this.filters.medium : [this.filters.medium]

        list = list.filter((c) => {
          return selectedMediums.some(
            (medium) =>
              c.medium?.trim().toLowerCase() === medium.trim().toLowerCase() ||
              c.medium1?.trim().toLowerCase() === medium.trim().toLowerCase() ||
              c.medium2?.trim().toLowerCase() === medium.trim().toLowerCase()
          )
        })
      }

      if (this.filters.level) {
        list = list.filter((c) => this.filters.level.includes(c.classLevel?.trim()))
      }

      if (this.filters.instructor) {
        list = list.filter((c) => {
          if (!Array.isArray(this.filters.instructor)) return false

          const selected = this.filters.instructor.map((x) => x.trim())

          const topMatch = selected.some((name) => {
            const n1 = instructorStore.instructorsById[c.instructorId]?.fullName?.trim()
            const n2 = instructorStore.instructorsById[c.instructor2Id]?.fullName?.trim()
            return n1 === name || n2 === name
          })

          if (topMatch) return true

          return (c.classDays || []).some((d) => {
            const n1 = instructorStore.instructorsById[d.instructor1Id]?.fullName?.trim()
            const n2 = instructorStore.instructorsById[d.instructor2Id]?.fullName?.trim()
            return selected.includes(n1) || selected.includes(n2)
          })
        })
      }

      if (this.filters.day) {
        list = list.filter((c) => {
          const validDays = c.classDays.filter((d) => this.filters.day.includes(d.classDay))
          return validDays.length > 0
        })
      }

      if (this.filters.availability) {
        list = list.filter((c) => {
          if (c.daySales) {
            return c.classDays.some((d) => d.limit - d.daysSold > 0)
          } else {
            return c.seatsAvailable > 0
          }
        })
      }

      if (this.filters.keywords) {
        list = list.filter((c) => classMatchesKeywordsFilter(c.keywords, this.filters.keywords))
      }

      if (this.sortBy) {
        list = list.sort((a, b) => {
          if (this.sortBy === 'code') return sortByCode(a, b)

          let valueA = a[this.sortBy]
          let valueB = b[this.sortBy]

          if (this.sortBy === 'startDateStandard') {
            const firstDay = (x) => (x.classDays?.length ? x.classDays.sort((a, b) => a.date - b.date)[0] : null)
            const firstDayA = firstDay(a)
            const firstDayB = firstDay(b)
            valueA = firstDayA ? firstDayA.date + firstDayA.stTime : a.startDateStandard
            valueB = firstDayB ? firstDayB.date + firstDayB.stTime : b.startDateStandard
          }

          if (typeof valueA === 'string' && typeof valueB === 'string') {
            return valueA.trim().toLowerCase().localeCompare(valueB.trim().toLowerCase())
          } else if (typeof valueA === 'number' && typeof valueB === 'number') {
            return valueA - valueB
          } else {
            return String(valueA).localeCompare(String(valueB))
          }
        })
      }

      return list
    },

    getRecommendedClasses(state) {
      return (currentClass, limit = 4) => {
        if (!currentClass) return []

        const currentMediums = [currentClass.medium, currentClass.medium1, currentClass.medium2]
          .filter(Boolean)
          .map((m) => m.toLowerCase())

        const currentDays = (currentClass.classDays || []).map((d) => d.classDay)
        const currentLevel = currentClass.classLevel
        const currentCost = currentClass.costMember || 0
        const currentMinAge = currentClass.minAge || 0
        const currentMaxAge = currentClass.maxAge || 100

        const scored = state.openClasses
          .filter((cls) => cls.catalogId !== currentClass.catalogId)
          .map((cls) => {
            let score = 0

            if (cls.department && cls.department === currentClass.department) score += 2

            const clsMediums = [cls.medium, cls.medium1, cls.medium2].filter(Boolean).map((m) => m.toLowerCase())
            if (clsMediums.some((m) => currentMediums.includes(m))) score += 3

            if (cls.classLevel && cls.classLevel === currentLevel) score += 1

            const clsDays = (cls.classDays || []).map((d) => d.classDay)
            if (clsDays.some((d) => currentDays.includes(d))) score += 1

            if (cls.costMember && Math.abs(cls.costMember - currentCost) <= currentCost * 0.2) score += 1

            const clsMinAge = cls.minAge || 0
            const clsMaxAge = cls.maxAge || 100
            const overlap = Math.min(currentMaxAge, clsMaxAge) - Math.max(currentMinAge, clsMinAge)
            if (overlap > 0) score += 2 // age overlap gives extra points

            return { cls, score }
          })

        return scored
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score || a.cls.startDateStandard.localeCompare(b.cls.startDateStandard))
          .slice(0, limit)
          .map(({ cls }) => cls)
      }
    },

    getRecommendedClassesByCartItems() {
      return (cartItems, limit = 4) => {
        const classesInCart = (cartItems || []).filter((item) => item.type === 'class')
        if (!classesInCart.length) return []

        const currentClass = classesInCart[classesInCart.length - 1]
        const excludeIds = classesInCart.map((c) => c.catalogId)

        return this.getRecommendedClasses(currentClass, limit + excludeIds.length)
          .filter((cls) => !excludeIds.includes(cls.catalogId))
          .slice(0, limit)
      }
    },
  },

  actions: {
    userInWhitelist(cls, defaultToTrue = true) {
      if (!cls.priorityGroups) return defaultToTrue
      const whiteListGroupIds = cls.priorityGroups
        .split(',')
        .map((g) => g.trim())
        .filter((val) => val !== '')
        .filter((g) => !isNaN(g))
      return Object.values(useUserStore()?.groupIds)?.some((value) => whiteListGroupIds.includes(value.toString()))
    },
    passesAccessChecks(cls) {
      if (!this.userInWhitelist(cls)) return false
      if (!cls.displayOnly) return true
      return useUserStore().isPreAuthorized || this.userInWhitelist(cls, false)
    },
    passesMembershipCheck(cls) {
      return !cls.membersOnly || useCartStore().canAddMembersOnlyItem
    },
    async sendMessageClass(classId, message) {
      try {
        await axios.post(`${siteConfig.apiBaseUrl}/classes/${classId}/message`, { message })
      } catch (error) {
        console.log('error - sendMessageClass', error)
      }
    },

    updateMembershipPricing() {
      if (!siteConfig.features.dynamicMemberPrice) return
      const userStore = useUserStore()
      const membership = userStore?.membershipsInHousehold?.find((m) => m.member_id === userStore?.account?.personId)
      const membershipsInCart = useCartStore()?.membershipsInCart

      membership ? (membership.categoryId = membership?.category_id) : null // Standardize categoryId property name
      const highestDiscount = [...membershipsInCart, membership]
        ?.filter((m) => m !== null && m !== undefined)
        ?.reduce((acc, m) => {
          const discount = siteConfig.memberships.find((mem) => mem.categoryId === m.categoryId)?.discounts?.['class']

          // Check if discount is defined and has an 'amount' property
          if (discount && typeof discount.amount === 'number') {
            // Initialize acc with the current object if acc is not defined
            if (!acc) {
              return { ...m, discount: discount.amount }
            }

            // Update acc if discount is greater than acc's discount
            if (discount.amount > acc.discount) {
              return { ...m, discount: discount.amount }
            }
          }

          return acc
        }, null)
      const discount = siteConfig.memberships.find((mem) => mem.categoryId === highestDiscount?.categoryId)
        ?.discounts?.['class'] || { amount: 0 }
      this.openClasses.forEach((cls) => {
        let discountAmount = discount.type === 'percent' ? cls.costNon * (discount.amount / 100) : discount.amount
        cls.costMember = parseFloat((cls.costNon - discountAmount).toFixed(2))
        cls.costMemberStr = String(cls.costMember)
        cls.memberCostFmt = String(cls.costMember.toFixed(2))
        cls.memberSavings = parseFloat((cls.costNon - cls.costMember).toFixed(2))
      })
      useCartStore()
        .items.filter((item) => item.type === 'class')
        .forEach((cls) => {
          let discountAmount =
            discount.type === 'percent' ? cls.nonMemberCost * (discount.amount / 100) : discount.amount
          cls.memberCost = parseFloat((cls.nonMemberCost - discountAmount).toFixed(2))
        })
    },
    async fetchReportRoster(classDetails) {
      this.loadingRoster = true

      try {
        const response = await axios.get(`${siteConfig.apiBaseUrl}/reports/roster/${classDetails.catalogId}/data`)

        this.roster = response.data

        this.loadingRoster = false
      } catch (error) {
        this.loadingRoster = false
        console.log('error - fetchReportRoster', error)
      }
    },

    resetFilters() {
      Object.keys(this.filters).forEach((key) => {
        this.filters[key] = null
      })
    },

    applyFilters(filters) {
      Object.keys(filters).forEach((key) => {
        this.filters[key] = filters[key]
      })
    },

    fetchRegistrationCounts() {
      const url = `${siteConfig.apiBaseUrl}/reports/queries/class-registration-counts`

      axios
        .get(url)
        .then((response) => {
          const data = response.data
          if (!data.ok) return
          const classCounts = {}
          const sessionCounts = {}
          data.results.forEach((item) => {
            if (item.schedule_id) {
              sessionCounts[item.schedule_id] = item.registration_count
            } else {
              classCounts[item.catalog_id] = item.registration_count
            }
          })
          this.classRegistrationCountsById = classCounts
          this.sessionRegistrationCountsById = sessionCounts
        })
        .catch((error) => {
          console.log('Error fetching registration counts', error)
        })
    },

    fetchInstructors() {
      this.loadingInstructors = true

      const req = axios.get(`${siteConfig.apiBaseUrl}/classes/instructors`)

      req
        .then((response) => {
          let instructors = response.data

          const instructorsById = {}
          instructors.forEach((instructor) => {
            instructorsById[instructor.instructorId] = instructor
            instructor.imageUrl = getInstructorImageUrl(instructor.imageUrl)
          })
          this.instructorsById = instructorsById

          if (siteConfig.features.instructorExclusions && siteConfig.features.instructorExclusions.length) {
            instructors = instructors.filter(
              (inst) => !siteConfig.features.instructorExclusions.includes(inst.fullName)
            )
          }

          this.instructors = instructors.sort((a, b) => {
            if (normalize(a.lastName) < normalize(b.lastName)) {
              return -1
            } else if (normalize(a.lastName) > normalize(b.lastName)) {
              return 1
            }
            return 0
          })

          this.loadingInstructors = false
        })
        .catch((error) => {
          this.loadingInstructors = false
          console.log('error', error)
        })
        .finally(() => {
          if (siteConfig.key === 'perkins') {
            this.fetchInstructorGroups()
          }
        })
    },

    async fetchInstructorGroups() {
      try {
        const response = await axios.get(`${siteConfig.apiBaseUrl}/reports/queries/perkins/instructor-groups`)

        const groups = response.data.results

        if (groups.length) {
          groups.forEach((group) => {
            this.groupsForInstructorId[group.person_id] = this.groupsForInstructorId[group.person_id] || []
            this.groupsForInstructorId[group.person_id].push(group.group_name)
          })
        }
      } catch (e) {
        console.log('fetchInstructorGroups error', e)
      }
    },

    fetchPrograms() {
      this.loadingPrograms = true

      // const req = axios.get(`${siteConfig.legacyApiBaseUrl}/classes/allprograms`, {
      //   auth: siteConfig.legacyApiAuth
      // })

      const req = axios.get(`${siteConfig.apiBaseUrl}/classes/programs`)

      req
        .then((response) => {
          const programs = response.data

          this.programs = response.data

          this.programs.forEach((p) => {
            this.programIdsByName[p.title] = p.eventId
          })

          this.loadingPrograms = false
        })
        .catch((error) => {
          this.loadingPrograms = false
          console.log('error', error)
        })
    },
    loadedClassById(id) {
      return this.openClasses?.find((c) => c.catalogId == id)
    },
    async fetchClassById(id, { throwOnError = false } = {}) {
      try {
        const loadedClass = this.loadedClassById(id)
        if (loadedClass && !siteConfig.features.fetchSimpleClasses) {
          return loadedClass
        }
        const response = await axios.get(`${siteConfig.apiBaseUrl}/class/${id}`)
        const classData = response.data
        // a missing class comes back as ok with 'Oh Darn' in an object
        const ok = classData.ok && !!classData.data?.catalogId

        if (!ok) {
          return null
        }

        // Format the single class
        const formattedClass = await this.formatClasses([classData.data])
        return formattedClass[0]
      } catch (error) {
        console.error('Error fetching class by ID:', error)
        if (throwOnError) throw error
        return null
      }
    },

    async formatClasses(classes) {
      const userStore = useUserStore()
      const instructorStore = useInstructorStore()

      // Add check to ensure instructors are loaded
      if (!instructorStore.instructors.length) {
        // Wait for instructors to load if they haven't been fetched yet
        await instructorStore.fetchInstructors()
      }

      let todaysDateStandard = getDateStandard()
      classes.forEach((cls) => {
        cls.images = []
        cls.imageUrl = getClassImageUrl(cls.imageUrl)
        cls.itemUrl = `/classes/${cls.catalogId}`

        cls.images.push(cls.imageUrl)

        if (presentImage(cls.imageUrl2)) {
          cls.imageUrl2 = getClassImageUrl(cls.imageUrl2)
          cls.images.push(cls.imageUrl2)
        }

        if (presentImage(cls.imageUrl3)) {
          cls.imageUrl3 = getClassImageUrl(cls.imageUrl3)
          cls.images.push(cls.imageUrl3)
        }
        if (presentImage(cls.imageUrl4)) {
          cls.imageUrl4 = getClassImageUrl(cls.imageUrl4)
          cls.images.push(cls.imageUrl4)
        }

        cls.startDateStandard = usDateFormatToYYYYMMDD(cls.startDate, 2000, false, 60)
        cls.endDateStandard = usDateFormatToYYYYMMDD(cls.endDate, 2000, false, 60)
        cls.registrationCutOffDateStandard = usDateFormatToYYYYMMDD(cls.registrationCutOff, 2000, false, 60)
        cls.hasStarted = todaysDateStandard > cls.startDateStandard
        cls.startsToday = todaysDateStandard === cls.startDateStandard
        cls.allowRegistrationAfterStart = resolveallowRegistrationAfterStart(cls)

        const registrationCloseMs = getRegistrationCloseMs()

        if (registrationCloseMs !== null && cls.startDate && cls.startTime && !cls.daySales) {
          const { year, month, day } = parseUsDate(cls.startDate)
          const time = parseTime12h(cls.startTime)

          if (time) {
            const { hours, minutes } = time
            const classStart = new Date(year, month, day, hours, minutes, 0, 0)
            const now = new Date()

            const msUntilClass = classStart.getTime() - now.getTime()
            cls.isPastCutOffDate = msUntilClass < registrationCloseMs
          } else {
            cls.isPastCutOffDate = false
          }
        } else if (cls.registrationCutOffDateStandard) {
          cls.isPastCutOffDate = cls.registrationCutOffDateStandard < todaysDateStandard
        } else {
          cls.isPastCutOffDate = false
        }

        cls.allowDeposit = cls.nonMemberDeposit > 0
        cls.payDepositOnly = false

        cls.ages = ''
        let ageUnitMin = cls.ageUnitMin || ''
        let ageUnitMax = cls.ageUnitMax || ''
        if (cls.minAge && cls.maxAge) {
          cls.ages += `${cls.minAge} ${ageUnitMin}`
          if (cls.maxAge >= 60) {
            if (cls.minAge < 18) {
              cls.ages += ` through adult`
            } else {
              cls.ages += `+`
            }
          } else {
            cls.ages += `-${cls.maxAge} ${ageUnitMax}`
          }
        } else if (cls.minAge && !cls.maxAge) {
          if (cls.minAge < 18) {
            cls.ages += `${cls.minAge} ${ageUnitMin}+`
          } else {
            cls.ages += `${cls.minAge} ${ageUnitMin}+`
          }
        }

        cls.ages = `Ages ${cls.ages}`

        if (siteConfig.key === 'snowfarm') {
          if (cls.ages === 'Ages 13 through adult') {
            cls.ages = 'Adults & Teens'
          } else if (cls.ages === 'Ages 18+') {
            cls.ages = 'Adults'
          }
        }

        if (siteConfig.features?.cleanDescriptionMarkup === false && cls.descr && cls.descr.includes('<')) {
          cls.description = cls.descr
        } else {
          cls.description = cleanMarkup(cls.descr)
        }

        cls.locationType = 'On-site'
        const onlineLocations = ['online', 'online class', 'zoom', 'webinar', 'webex', 'google meet', 'microsoft teams']
        if (cls.location?.toLowerCase().includes('off-site') || cls.location?.toLowerCase().includes('off site')) {
          cls.locationType = 'Off-site'
        } else if (onlineLocations.includes(cls.location?.toLowerCase())) {
          cls.locationType = 'Online'
        }

        cls.originalClassDayCount = cls.classDays?.length
        cls.unfilteredClassDays = cls.classDays

        const futurePublishedDays =
          cls.unfilteredClassDays?.filter((d) => {
            const dayDateStandard = usDateFormatToYYYYMMDD(d.dateFmt)
            return dayDateStandard >= todaysDateStandard && d.available && d.publish
          }) || []
        const ignoreRegistrationCloseDate = hasOnlyPreStartSeriesCloses(cls, futurePublishedDays)

        cls.classDays = cls.classDays?.filter((d) => {
          const dayDateStandard = usDateFormatToYYYYMMDD(d.dateFmt)
          const isFuture = dayDateStandard >= todaysDateStandard
          const isAvailable = d.available && d.publish

          d.isPastCutOffDate = resolveClassDayIsPastCutOffDate(d, { ignoreRegistrationCloseDate })

          return isFuture && isAvailable && !d.isPastCutOffDate
        })

        // Per-session registrationCloseDate can close a class even when class-level
        // registrationCutOff is empty (e.g. Nimbus Daytime Art Club / ACCHD-9871).
        if (!cls.isPastCutOffDate && futurePublishedDays.length > 0 && !cls.classDays?.length) {
          cls.isPastCutOffDate = true
        }

        cls.classDays?.forEach((d) => {
          d.dateStandard = usDateFormatToYYYYMMDD(d.dateFmt)
        })

        cls.classDays = cls.classDays?.sort(sortClassDaysByStart) || []

        // Keep weekday / session count in the listing even when registration cutoff
        // has cleared all registrable classDays.
        const summaryClassDays = [...futurePublishedDays].sort(sortClassDaysByStart)

        let allClassDays = summaryClassDays?.map((day) => ({
          day: day.classDay,
          dayOfWeek: day.dayOfWeek,
          date: day.dateFmt,
          startTime: day.startTimeFmt,
          endTime: day.endTimeFmt,
        }))
        const uniqueStartTimes = Array.from(new Set(allClassDays?.map((d) => d.startTime)))
        const uniqueEndTimes = Array.from(new Set(allClassDays?.map((d) => d.endTime)))
        const uniqueClassDates = Array.from(new Set(allClassDays?.map((d) => d.date)))
        const uniqueClassDays = Array.from(new Set(allClassDays?.map((d) => d.day)))
        // NOTE: This breaks the actual order of the class days for classes with multiple days. Unsure the consiquences of removing this for classes > 7 days.
        // .sort((a, b) => {
        //   if (daySortPos[a] < daySortPos[b]) return -1
        //   if (daySortPos[a] > daySortPos[b]) return 1
        //   return 0
        // })

        cls.uniqueClassDays = uniqueClassDays

        let instructorExclusions = []
        if (siteConfig.features.instructorExclusions && siteConfig.features.instructorExclusions.length) {
          instructorExclusions = siteConfig.features.instructorExclusions
        }

        cls.instructorSummary = ''

        if (!instructorExclusions.includes(cls.instructorName)) {
          cls.instructorSummary += cls.instructorName
        }

        if (cls.instructor2Id) {
          const instructor2 = instructorStore.instructorsById[cls.instructor2Id]
          if (instructor2?.fullName && !instructorExclusions.includes(instructor2.fullName)) {
            if (cls.instructorSummary) {
              cls.instructorSummary += ` and ${instructor2.fullName}`
            } else {
              cls.instructorSummary += instructor2.fullName
            }
          }
        }

        cls.classDaySummary = null
        if (!siteConfig.features.hideClassDay) {
          if (cls.daySales) {
            if (uniqueClassDays.length === 1) {
              if (uniqueClassDates.length > 1) {
                cls.classDaySummary = uniqueClassDays[0] + 's'
              } else {
                cls.classDaySummary = uniqueClassDays[0]
              }
            } else {
              cls.classDaySummary = 'Various days'
            }
            // if (cls.classDays.length > 1) {
            //   cls.classDaySummary = 'Various days'
            // } else if (cls.classDays.length === 0) {
            //   cls.classDaySummary = uniqueClassDays[0]
            // }
          } else if (uniqueClassDates.length === 1) {
            cls.classDaySummary = uniqueClassDays[0]
          } else if (uniqueClassDays.length === 1) {
            if (cls.sessions > 1) {
              cls.classDaySummary = uniqueClassDays[0] + 's'
            } else {
              cls.classDaySummary = uniqueClassDays[0]
            }
          } else if (uniqueClassDays.length === 2) {
            if (cls.sessions == uniqueClassDays.length) {
              cls.classDaySummary = `${uniqueClassDays[0]} and ${uniqueClassDays[1]}`
            } else {
              cls.classDaySummary = `${uniqueClassDays[0]}s and ${uniqueClassDays[1]}s`
            }
          } else if (uniqueClassDays.length === 5) {
            cls.classDaySummary = `${uniqueClassDays[0]} – ${uniqueClassDays[uniqueClassDays.length - 1]}`
          } else if (uniqueClassDays.length > 2) {
            if (cls.sessions == uniqueClassDays.length) {
              cls.classDaySummary = uniqueClassDays.join(', ')
            } else {
              cls.classDaySummary = uniqueClassDays.map((d) => `${d}s`).join(', ')
            }
          } else {
            cls.classDaySummary = ''
          }
        }

        if (siteConfig.features.classDateOverrides && siteConfig.features.classDateOverrides[cls.code]) {
          cls.dateSummary = siteConfig.features.classDateOverrides[cls.code]
        } else {
          if (cls.classDaySummary) {
            if (cls.daySales && cls.classDays.length) {
              cls.dateSummary = `${cls.classDaySummary}, ${timestampsToDateRange(
                cls.classDays[0].dateFmt,
                cls.endDate
              )}`
            } else {
              cls.dateSummary = `${cls.classDaySummary}, ${timestampsToDateRange(cls.startDate, cls.endDate)}`
            }
          } else {
            cls.dateSummary = `${timestampsToDateRange(cls.startDate, cls.endDate)}`
          }

          if (!cls.daySales && cls.classDaySummary && cls.sessions > 1 && !siteConfig.features.hideClassSessionCount) {
            cls.dateSummary += ` (${cls.sessions} sessions)`
          }
        }

        if (siteConfig.features.hideClassTimes) {
          cls.timeSummary = null
        } else if (siteConfig.features.classTimeOverrides && siteConfig.features.classTimeOverrides[cls.code]) {
          cls.timeSummary = siteConfig.features.classTimeOverrides[cls.code]
        } else if (
          siteConfig.features.showMixedClassTimes &&
          (uniqueStartTimes.length > 1 || uniqueEndTimes.length > 1)
        ) {
          const grouped = {}
          summaryClassDays.forEach((d) => {
            const time = `${d.startTimeFmt} - ${d.endTimeFmt}`
            if (!grouped[time]) grouped[time] = []
            grouped[time].push(usDateFormatToShortMonthAndDay(d.dateFmt))
          })
          cls.timeSummary = Object.keys(grouped)
            .map((time) => `${time} (${grouped[time].join(', ')})`)
            .join('; ')
        } else {
          if (
            !cls.daySales &&
            summaryClassDays.length > 1 &&
            summaryClassDays.length <= 3 &&
            (uniqueStartTimes.length > 1 || uniqueEndTimes.length > 1)
          ) {
            cls.timeSummary = summaryClassDays
              .map((d) => `${d.classDay} ${d.startTimeFmt} - ${d.endTimeFmt}`)
              .join(', ')
          } else if (uniqueStartTimes.length > 1 || uniqueEndTimes.length > 1) {
            cls.timeSummary = 'Various times'
          } else if (cls.daySales && cls.classDays.length) {
            cls.timeSummary = `${cls.classDays[0].startTimeFmt} – ${cls.classDays[0].endTimeFmt}`
          } else {
            cls.timeSummary = `${cls.startTime} – ${cls.endTime}`
          }
        }

        if (cls.daySales) {
          let openSlots = 0
          cls.classDays.forEach((day) => {
            if (day.limit - day.daysSold > 0) {
              openSlots += day.limit - day.daysSold
            }
          })
          cls.classIsFull = openSlots <= 0

          if (cls.fee) {
            cls.feePerSession = parseFloat((cls.fee / cls.classDays.length).toFixed(2))
          } else {
            cls.feePerSession = 0
          }
        } else {
          cls.classIsFull = cls.seatsAvailable <= 0
        }

        const membership = userStore?.membershipsInHousehold?.find((m) => m.member_id === userStore?.account.personId)

        if (membership) {
          const discount = siteConfig.memberships?.find((c) => c.categoryId === membership.category_id)?.discounts?.[
            'class'
          ]
          if (discount) {
            applyMembershipDiscount(cls, discount)
          }
        }
        cls.canAddEntireSeries = resolveCanAddEntireSeries(cls)
      })
      return classes
    },

    async fetchOpenSemesters() {
      try {
        const response = await axios.get(`${siteConfig.apiBaseUrl}/classes/semesters/open`)
        this.openSemesters = new Map((response.data?.data || []).map((semester) => [semester.semesterId, semester]))
      } catch (error) {
        console.error('Error fetching open semesters:', error)
      }
    },

    async fetchOpenClasses() {
      const instructorStore = useInstructorStore()

      if (this.openClasses.length) {
        return
      }

      if (this.openClassesFetch) {
        return this.openClassesFetch
      }

      let releaseSharedFetch
      this.openClassesFetch = new Promise((resolve) => {
        releaseSharedFetch = resolve
      })

      this.loadingClasses = true

      try {
        const params = new URLSearchParams()
        if (siteConfig.features.fetchSimpleClasses) {
          params.append('simple', 'true')
        }

        const response = await axios.get(`${siteConfig.apiBaseUrl}/classes/open?${params}`)
        let openClasses = response.data

        if (siteConfig.features.classFilters?.semesterWhitelist) {
          openClasses = openClasses.filter((c) =>
            siteConfig.features.classFilters?.semesterWhitelist?.includes(c.semesterId)
          )
        }

        let todaysDateStandard = getDateStandard()

        openClasses = await this.formatClasses(openClasses)
        openClasses = openClasses.filter((c) => !c.cancelled)

        if (siteConfig.features?.hideStartedClasses) {
          openClasses = openClasses.filter((c) => {
            if (!c.daySales) {
              return c.startDateStandard >= todaysDateStandard
            } else {
              const validDays = c.classDays.filter((d) => {
                return d.dateStandard >= todaysDateStandard
              })
              return validDays.length > 0
            }
          })
        }

        if (siteConfig.features?.hideEndedClasses) {
          openClasses = openClasses.filter((c) => {
            return c.endDateStandard >= todaysDateStandard
          })
        }

        if (siteConfig.features?.hideStartedClassesAfterDays) {
          const days = siteConfig.features.hideStartedClassesAfterDays
          const cutoff = new Date()
          cutoff.setDate(cutoff.getDate() - days)
          const cutoffDateStandard = getDateStandard(cutoff.toISOString())
          openClasses = openClasses.filter((c) => c.startDateStandard >= cutoffDateStandard)
        }

        const programMediums = []
        const secondaryMediums = []
        const categories = []
        const semesters = []
        const levels = []
        const openClassInstructorIds = []
        const activeClassInstructorIds = []
        const openClassProgramIds = []
        const locations = []
        let sites = [] // XXX

        if (siteConfig.key === 'perkins') {
          sites = ['Collingswood', 'Moorestown', 'Evesham']
        }

        openClasses.forEach((cls) => {
          if (cls.eventId && !openClassProgramIds.includes(cls.eventId)) {
            openClassProgramIds.push(cls.eventId)
          }

          let allMediaOk = siteConfig.features.keepAllMedia

          if (cls.medium && !programMediums.includes(cls.medium)) {
            if (cls.medium.trim() !== 'None' && (allMediaOk || (cls.medium1 && cls.medium1.trim() !== 'All Media'))) {
              programMediums.push(cls.medium)
            } else {
              cls.mediumWas = cls.medium
              cls.medium = ''
            }
          }
          if (cls.medium1 && !secondaryMediums.includes(cls.medium1)) {
            if (cls.medium1.trim() !== 'None' && (allMediaOk || cls.medium1.trim() !== 'All Media')) {
              secondaryMediums.push(cls.medium1)
            }
          }
          if (cls.medium2 && !secondaryMediums.includes(cls.medium2)) {
            if (cls.medium2.trim() !== 'None' && (allMediaOk || cls.medium2.trim() !== 'All Media')) {
              secondaryMediums.push(cls.medium2)
            }
          }

          const department = cls.department?.trim()
          if (department && !categories.includes(department)) {
            categories.push(department)
          }

          const semesterName = cls.semesterName?.trim()
          if (semesterName && !semesters.includes(semesterName)) {
            semesters.push(semesterName)
          }

          let level = cls.classLevel?.trim()
          if (level && level !== '-1' && !levels.includes(level)) {
            levels.push(level)
          }

          const location = cls.location?.trim()
          if (location && !locations.includes(location)) {
            if (!location.toLowerCase().includes('all')) {
              locations.push(location)
            }
          }

          if (cls.location && siteConfig.key === 'perkins') {
            if (cls.location.indexOf('CW') === 0 || cls.location.indexOf('Collingswood') === 0) {
              cls.site = 'Collingswood'
            } else if (cls.location.indexOf('MT') === 0 || cls.location.indexOf('Moorestown') === 0) {
              cls.site = 'Moorestown'
            } else if (cls.location.indexOf('EV') === 0 || cls.location.indexOf('Evesham') === 0) {
              cls.site = 'Evesham'
            }
          }

          if (cls.instructorId && cls.instructorId !== 0) {
            if (!openClassInstructorIds.includes(cls.instructorId)) {
              if (!cls.isPastCutOffDate || siteConfig.features?.showClassesAfterCutOffDate) {
                openClassInstructorIds.push(cls.instructorId)
              }
            }
            if (!activeClassInstructorIds.includes(cls.instructorId)) {
              activeClassInstructorIds.push(cls.instructorId)
            }
          }
          if (cls.instructor2Id && cls.instructor2Id !== 0) {
            if (!openClassInstructorIds.includes(cls.instructor2Id)) {
              if (!cls.isPastCutOffDate || siteConfig.features?.showClassesAfterCutOffDate) {
                openClassInstructorIds.push(cls.instructor2Id)
              }
            }
            if (!activeClassInstructorIds.includes(cls.instructor2Id)) {
              activeClassInstructorIds.push(cls.instructor2Id)
            }
          }
          if (Array.isArray(cls.classDays)) {
            cls.classDays.forEach((d) => {
              if (d.instructor1Id && d.instructor1Id !== 0 && !openClassInstructorIds.includes(d.instructor1Id)) {
                openClassInstructorIds.push(d.instructor1Id)
              }
              if (d.instructor2Id && d.instructor2Id !== 0 && !openClassInstructorIds.includes(d.instructor2Id)) {
                openClassInstructorIds.push(d.instructor2Id)
              }
            })
          }
        })

        this.keywords = collectUniqueKeywords(openClasses)

        // if (siteConfig.features.separateClassesByDivision) {
        //   this.openWorkshops = openClasses.filter(c => c.division === 'workshop')
        //   this.openClasses = openClasses.filter(c => c.division === 'class')
        // } else {
        //   this.openClasses = openClasses
        // }

        // Compose feature sorts in one pass. Applying open-above-closed, then
        // available-above-full with a date-only comparator, used to put closed
        // classes with seats back among open ones by start date (ACCHD-9871).
        if (siteConfig.features.showAvailableClassesAboveFull || siteConfig.features.showOpenClassesAboveClosed) {
          const comparator = buildClassListComparator()

          if (siteConfig.features.showAvailableClassesAboveFull) {
            const available = openClasses.filter((c) => c.seatsAvailable > 0).sort(comparator)
            const full = openClasses.filter((c) => c.seatsAvailable <= 0).sort(comparator)
            openClasses = [...available, ...full]
          } else {
            openClasses = openClasses.sort(comparator)
          }
        }

        if (siteConfig.features.sortClassesBySemesterRank) {
          await this.fetchOpenSemesters()
          openClasses = openClasses.sort((a, b) => {
            const aRank = this.openSemesters.get(a.semesterId)?.rank || 0
            const bRank = this.openSemesters.get(b.semesterId)?.rank || 0
            if (aRank > bRank) return 1
            if (aRank < bRank) return -1

            return 0
          })
        }

        this.openClasses = openClasses

        this.programMediums = programMediums.map((m) => m.trim()).sort()
        this.secondaryMediums = secondaryMediums.map((m) => m.trim()).sort()

        let allMediums = uniq([...this.programMediums, ...this.secondaryMediums])
          .map((m) => capitalizeFirstLetter(m))
          .sort()

        // XXX Should we also treat "All Media" as an exclusion instead of giving it special treatment?
        if (siteConfig.features?.mediumExclusions) {
          allMediums = allMediums.filter((m) => !siteConfig.features.mediumExclusions.includes(m))
        }

        this.mediums = allMediums

        if (siteConfig.features.categoryOrder) {
          const order = siteConfig.features.categoryOrder
          this.categories = categories.sort((a, b) => {
            if (order.indexOf(a) > order.indexOf(b)) return 1
            if (order.indexOf(a) < order.indexOf(b)) return -1
            return 0
          })
        } else {
          this.categories = categories.sort()
        }

        this.semesters = semesters.sort()

        if (siteConfig.features.classLevelOrder) {
          const order = siteConfig.features.classLevelOrder
          this.levels = levels.sort((a, b) => {
            if (order.indexOf(a) > order.indexOf(b)) return 1
            if (order.indexOf(a) < order.indexOf(b)) return -1
            return 0
          })
        } else {
          this.levels = levels.sort()
        }

        this.locations = locations.sort()
        this.sites = sites.sort()

        this.openClassProgramIds = openClassProgramIds
        instructorStore.updateOpenClassInstructorIds(openClassInstructorIds)
        instructorStore.updateActiveClassInstructorIds(activeClassInstructorIds)

        this.updateMembershipPricing()
        this.loadingClasses = false
      } catch (error) {
        this.loadingClasses = false
        console.log('error', error)
      } finally {
        releaseSharedFetch()
        this.openClassesFetch = null
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useClassStore, import.meta.hot))
}
